import { Fragment, useEffect, useState } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { CaretToggle } from '@snapi/luxury';

interface DropdownOption<T> {
  name: string;
  value: T;
}

interface DropdownProps<T> {
  label?: string;
  current: T;
  options: DropdownOption<T>[];
  onChange: (value: T) => void;
  horizontal?: boolean;
  disabled?: boolean;
}

export function Dropdown<T>({
  current,
  label,
  disabled,
  horizontal,
  options,
  onChange,
}: DropdownProps<T>) {
  const [selected, setSelected] = useState(
    options.find((o) => o.value === current)
  );

  useEffect(() => {
    setSelected(options.find((o) => o.value === current));
  }, [current, options]);

  const handleChange = (value: DropdownOption<T>) => {
    setSelected(options.find((o) => o.value === current));
    onChange(value.value);
  };

  return (
    <div
      className={clsx('flex flex-col flex-1', horizontal && 'sm:items-center')}
    >
      <div
        className={clsx('flex flex-col relative', horizontal && 'sm:flex-row')}
      >
        {label && (
          <>
            <label
              className={clsx('flex flex-1 mb-2', horizontal && 'sm:hidden')}
            >
              <h5>{label}</h5>
            </label>
            {horizontal && (
              <label className="hidden sm:block absolute top-5 -left-[200px] right-[288px] text-right">
                <h5>{label}</h5>
              </label>
            )}
          </>
        )}
        <div
          className={clsx('flex flex-col flex-1', horizontal && 'sm:w-[280px]')}
        >
          <Listbox
            value={selected}
            onChange={handleChange}
            disabled={disabled}
            horizontal
          >
            {({ open }) => (
              <div className="relative mt-1">
                <Listbox.Button className="relative rounded-[10px] h-[60px] w-full bg-white border border-gray-soft px-5 text-left disabled:bg-gray-soft">
                  <span className="flex">
                    <span className="flex flex-1 truncate">
                      {selected?.name}
                    </span>
                    {!disabled && (
                      <span className="self-center w-4">
                        <CaretToggle open={open} />
                      </span>
                    )}
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute -mt-4 pt-4 max-h-60 w-full overflow-auto bg-white border border-t-0 border-gray-soft rounded-b-[10px] divide-y divide-gray-soft z-[10]">
                    <span />
                    {options.map((option, personIdx) => (
                      <Listbox.Option
                        key={personIdx}
                        className={({ active }) =>
                          clsx(
                            'cursor-pointer select-none px-5 py-2 h-[44px] items-center',
                            active && 'bg-green-light'
                          )
                        }
                        value={option}
                      >
                        <span className="truncate">{option.name}</span>
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            )}
          </Listbox>
        </div>
      </div>
    </div>
  );
}
